/**
 * Shared PDF field components for user-fillable areas in printed documents.
 *
 * Primitives:
 *   - InlineField — underlined value inline mid-sentence
 *   - FillField — underline filling remaining horizontal space
 *   - YesNoField — checkbox pair
 *
 * Wrappers:
 *   - WithInlineResponse — label + response on the same row
 *   - WithNewlineResponse — text above, response indented below
 */

import { StyleSheet, Text, View } from '@react-pdf/renderer'
import type { ReactNode } from 'react'

import { mm } from '@/frontend/features/printing/pdf-utils'

const styles = StyleSheet.create({
  underline: {
    borderBottomWidth: 0.5,
    borderBottomColor: '#999',
    paddingHorizontal: mm(1),
    paddingBottom: mm(1),
    fontSize: 10,
  },
  checkboxRow: { flexDirection: 'row', alignItems: 'center', gap: mm(1.5) },
  checkboxPair: { flexDirection: 'row', alignItems: 'center', gap: mm(5) },
  checkboxBox: {
    width: mm(3.5),
    height: mm(3.5),
    borderWidth: 0.5,
    borderColor: '#333',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxTick: { fontSize: 8, fontFamily: 'Helvetica-Bold' },
  checkboxLabel: { fontSize: 10 },
  radioOuter: {
    width: mm(3.5),
    height: mm(3.5),
    borderRadius: mm(1.75),
    borderWidth: 0.5,
    borderColor: '#333',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  radioInner: {
    width: mm(2),
    height: mm(2),
    borderRadius: mm(1),
    backgroundColor: '#333',
  },
})

/**
 * Underlined value for use inline in a sentence.
 * Renders with a bottom border and a configurable minimum width.
 */
export function InlineField({ value, minWidth = mm(30) }: { value?: string; minWidth?: number }) {
  if (value) {
    return <Text style={{ fontSize: 10 }}>{value}</Text>
  }
  return <Text style={{ ...styles.underline, minWidth }}> </Text>
}

/**
 * Underline that fills remaining horizontal space on a row.
 * Use inside a `flexDirection: 'row'` parent. When `value` is provided,
 * renders plain text without the underline (for pre-filled data).
 */
export function FillField({ value, minHeight }: { value?: string; minHeight?: number }) {
  if (value) {
    return <Text style={{ fontSize: 10, flex: 1 }}>{value}</Text>
  }
  return (
    <Text style={{ ...styles.underline, flex: 1, ...(minHeight ? { minHeight } : {}) }}> </Text>
  )
}

/** Single checkbox with label. */
function Checkbox({ checked, label }: { checked: boolean; label: string }) {
  return (
    <View wrap={false} style={styles.checkboxRow}>
      <View style={styles.checkboxBox}>
        {checked && <Text style={styles.checkboxTick}>✓</Text>}
      </View>
      <Text style={styles.checkboxLabel}>{label}</Text>
    </View>
  )
}

/** Single checkbox with label. */
export function CheckboxField({ checked, label }: { checked: boolean; label: string }) {
  return (
    <View wrap={false} style={styles.checkboxRow}>
      <View style={styles.checkboxBox}>
        {checked && <Text style={styles.checkboxTick}>✓</Text>}
      </View>
      <Text style={styles.checkboxLabel}>{label}</Text>
    </View>
  )
}

/** Single radio button with label. */
export function RadioField({ checked, label }: { checked: boolean; label: string }) {
  return (
    <View wrap={false} style={styles.checkboxRow}>
      <View style={styles.radioOuter}>{checked && <View style={styles.radioInner} />}</View>
      <Text style={styles.checkboxLabel}>{label}</Text>
    </View>
  )
}

/**
 * Radio group that renders all options with the selected one filled.
 * Expects options as `{ value: string; label: string }[]`.
 */
export function RadioGroupField({
  value,
  options,
}: {
  value: string
  options: readonly { value: string; label: string }[]
}) {
  return (
    <View>
      {options.map((option) => (
        <RadioField key={option.value} checked={value === option.value} label={option.label} />
      ))}
    </View>
  )
}

/** Yes / No checkbox pair. */
export function YesNoField({ value }: { value: 'yes' | 'no' | '' }) {
  return (
    <View style={styles.checkboxPair}>
      <Checkbox checked={value === 'yes'} label="Yes" />
      <Checkbox checked={value === 'no'} label="No" />
    </View>
  )
}

/**
 * Horizontal layout wrapper — label on the left, response on the right
 * filling the remaining space. Used for signature blocks, name fields, etc.
 */
export function WithInlineResponse({
  children,
  response,
}: {
  children: ReactNode
  response: ReactNode
}) {
  return (
    <View
      wrap={false}
      style={{
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: mm(3),
      }}
    >
      {children}
      {response}
    </View>
  )
}

/** Empty spacer View for controlling vertical gaps between PDF elements. */
export function Spacer({ height }: { height: number }) {
  return <View style={{ height }} />
}

/**
 * Vertical layout wrapper — text above, response indented below on a new line.
 * Used for questions that need a Yes/No or other answer beneath them.
 */
export function WithNewlineResponse({
  children,
  response,
}: {
  children: ReactNode
  response: ReactNode
}) {
  return (
    <View wrap={false}>
      {children}
      <View style={{ paddingLeft: mm(4), marginTop: mm(1) }}>{response}</View>
    </View>
  )
}
